/** The session, and the mutations that start and end it. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import * as authApi from '../api/Auth';
import { ApiError } from '../api/errors';
import { parsed } from '../api/parse';
import { sessionSchema, vocabularySchema, type Session } from '../schemas';
import { keys } from './keys';

/**
 * Who is signed in. Typed `Session | null` because signing out writes null into
 * this key: signed out is a known answer, not an absent one. A 401 is final;
 * anything else is retried, since the API may still be restarting.
 */
export function useSessionQuery() {
	return useQuery({
		queryKey: keys.auth.session(),
		queryFn: async (): Promise<Session | null> =>
			parsed(sessionSchema, await authApi.getSession(), '/auth/me'),
		retry: (count, error) =>
			!(error instanceof ApiError && error.status === 401) && count < 5,
		retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
		staleTime: Infinity,
	});
}

export function useVocabularyQuery(enabled: boolean) {
	return useQuery({
		queryKey: keys.auth.vocabulary(),
		queryFn: async () =>
			parsed(
				vocabularySchema,
				await authApi.getVocabulary(),
				'/reference/vocabulary',
			),
		enabled,
		staleTime: Infinity,
		/* Not fatal: every component falls back to humanising the enum value. */
		retry: false,
	});
}

export function useRegistrationOpenQuery() {
	return useQuery({
		queryKey: keys.auth.registrationOpen(),
		queryFn: async () =>
			parsed(
				z.object({ open: z.boolean() }),
				await authApi.getRegistrationOpen(),
				'/auth/registration-open',
			),
	});
}

export function useSignInMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { email: string; password: string }) =>
			parsed(
				sessionSchema,
				await authApi.signIn(input.email, input.password),
				'/auth/login',
			),
		onSuccess: (session) => {
			client.setQueryData(keys.auth.session(), session);
		},
	});
}

export function useSignOutMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: () => authApi.signOut(),
		/* `onSettled`, not `onSuccess`: a failed logout request still means the
       person intends to be signed out here. */
		onSettled: () => {
			client.removeQueries({
				predicate: (query) => query.queryKey[0] !== 'auth',
			});
			client.setQueryData(keys.auth.session(), null);
			client.removeQueries({ queryKey: keys.auth.vocabulary() });
		},
	});
}

/** Creating the firm's first account. */
export function useRegisterMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			firmName: string;
			name: string;
			email: string;
			password: string;
		}) =>
			parsed(
				sessionSchema,
				await authApi.register(input),
				'/auth/register',
			),
		onSuccess: (session) => {
			client.setQueryData(keys.auth.session(), session);
		},
	});
}

export function useAcceptInviteMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			token: string;
			name: string;
			password: string;
		}) =>
			parsed(
				sessionSchema,
				await authApi.acceptInvite(input),
				'/auth/accept-invite',
			),
		onSuccess: (session) => {
			client.setQueryData(keys.auth.session(), session);
		},
	});
}

export function useForgotPasswordMutation() {
	return useMutation({
		mutationFn: (input: { email: string }) => authApi.forgotPassword(input),
	});
}

export function useResetPasswordMutation() {
	return useMutation({
		mutationFn: (input: { token: string; password: string }) =>
			authApi.resetPassword(input),
	});
}
